import * as React from "react"
import * as paths from "constants/paths"
import SummaryCardsBenchmark from "../../SummaryCards/SummaryCardsBenchmark"
import NavTabs from "components/Shared/NavTabs"
import { isFeatureEnabled } from "config/feature-flags"
import { useIntl } from "react-intl"
import { mapGlobalFilters } from "components/Shared/GlobalFilter/utils"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { globalFiltersMapBackendConfig } from "components/Shared/GlobalFilter/constants"
import {
	BenchmarkTemplateProps,
	LevelName,
} from "components/Transactional/Performance/interfaces"
import BenchmarkTab from "components/Transactional/Performance/components/BenchmarkTab"
import {
	formatInsightSubtitle,
	generateInsightTitle,
	getLocationLevel,
	getBrandLevel,
	getTabTitle,
	getSubtitleByFilters,
} from "components/Transactional/Performance/utils/utils"
import {
	BENCHMARK_TABLE_TITLE_ID,
	COLUMN_NAMES,
	METRICS,
	SUMMARY_COMPONENT_FEATURE_FLAG_ID,
	TIMELINE_DOUBLE_AXIS_COMPONENT_FEATURE_FLAG_ID,
	textBenchmarkByBrand,
	textBenchmarkByChannel,
	textBenchmarkByFormat,
	textBenchmarkByLocation,
	textTotalSalesTab,
	TIMELINE_GROWTH_COMPONENT_FEATURE_FLAG_ID,
} from "components/Transactional/Performance/constants"
import Timeline from "components/Transactional/Performance/components/Timeline"

const BenchmarkCommonTemplate = (props: BenchmarkTemplateProps) => {
	const {
		period,
		dateInterval,
		levelName,
		metricName,
		onChangeLevelName,
		filterLevelSelected,
	} = props

	const intl = useIntl()
	const { filters: globalFilters } = useFiltersContext()

	const benchmarkGlobalFilters = mapGlobalFilters(
		globalFilters,
		globalFiltersMapBackendConfig
	)

	const filtersBenchmark = {
		...benchmarkGlobalFilters,
		period,
		metricName,
		...dateInterval,
	}

	const title = generateInsightTitle(
		BENCHMARK_TABLE_TITLE_ID,
		levelName,
		getSubtitleByFilters(filterLevelSelected, intl),
		intl,
		filtersBenchmark.metricName
	)

	const generateBenchmarkTabs = () => {
		return [
			{
				text: getTabTitle(textTotalSalesTab, levelName, intl),
				pathname:
					paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_TOTAL_SALES,
				component: () =>
					generateTabComponent(METRICS.BY_LEVEL, levelName, true),
			},
			{
				text: getTabTitle(
					textBenchmarkByBrand,
					levelName,
					intl,
					filtersBenchmark,
					METRICS.BY_BRAND
				),
				pathname: paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_BRANDS,
				component: () =>
					generateTabComponent(
						METRICS.BY_BRAND,
						getBrandLevel(benchmarkGlobalFilters),
						true
					),
			},
			{
				text: getTabTitle(textBenchmarkByChannel, levelName, intl),
				pathname:
					paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_CHANNELS,
				component: () =>
					generateTabComponent(METRICS.BY_CHANNEL, COLUMN_NAMES.CHANNEL, false),
			},
			{
				text: getTabTitle(textBenchmarkByFormat, levelName, intl),
				pathname: paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_FORMATS,
				component: () =>
					generateTabComponent(METRICS.BY_FORMAT, COLUMN_NAMES.FORMAT, true),
			},
			{
				text: getTabTitle(
					textBenchmarkByLocation,
					levelName,
					intl,
					filtersBenchmark,
					METRICS.BY_LOCATION
				),
				pathname:
					paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_LOCATION,
				component: () =>
					generateTabComponent(
						METRICS.BY_LOCATION,
						getLocationLevel(
							benchmarkGlobalFilters["region"],
							benchmarkGlobalFilters["county"],
							benchmarkGlobalFilters["store"]
						),
						true
					),
				active: isFeatureEnabled("benchmarkLocationTab"),
			},
		]
	}

	const generateTabComponent = (
		metricName: string,
		levelName: LevelName,
		hasPaginator: boolean
	) => {
		return (
			<BenchmarkTab
				filtersBenchmark={filtersBenchmark}
				setLevelName={onChangeLevelName}
				levelName={levelName}
				metricName={metricName}
				hasPaginator={hasPaginator}
			/>
		)
	}

	const renderSubTitle = () => {
		const subTitle = formatInsightSubtitle(
			levelName,
			getSubtitleByFilters(filterLevelSelected, intl),
			filtersBenchmark.period,
			intl,
			filtersBenchmark.metricName
		)

		return <div className="sub-title">{subTitle}</div>
	}

	return (
		<>
			<SummaryCardsBenchmark
				isVisible={isFeatureEnabled(SUMMARY_COMPONENT_FEATURE_FLAG_ID)}
				filtersBenchmark={filtersBenchmark}
			/>
			<Timeline
				level={levelName}
				template={"growth"}
				isVisible={isFeatureEnabled(TIMELINE_GROWTH_COMPONENT_FEATURE_FLAG_ID)}
			/>
			<Timeline
				level={levelName}
				template={"double"}
				isVisible={isFeatureEnabled(
					TIMELINE_DOUBLE_AXIS_COMPONENT_FEATURE_FLAG_ID
				)}
			/>
			<NavTabs
				title={title}
				subtitleContent={renderSubTitle()}
				tabs={generateBenchmarkTabs()}
			/>
		</>
	)
}

export default BenchmarkCommonTemplate
